// @flow

// Types
import type {
  CommentOperation,
} from '../../types'

import type {
  Comment,
} from 'hfty-model-comments/types'

// Libs
import _map from 'lodash/map'
import _every from 'lodash/every'

// utils
import {
  convertCommentsToBackendUnits,
} from 'hfty-comments'

// Operator
import operator from '../operator'

// selectors
import { currentUserUnits$ } from 'hfty-users'
import { crops$ } from 'fc-model-crops'

// Controller
import controller from 'hfty-ctrl-comments'

// Effect Managers
import { call, fork, put, race, select, take } from 'redux-saga/effects'


type OperationsArgs = {
  operations$: (state: Object) => Array<CommentOperation>
}

// Exports
export default function *watchOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  while (true) {
    yield take([operator.actionTypes.add(), operator.actionTypes.delete()])
    yield fork(handleOperations, {operations$})
  }
}

export function *handleOperations ({operations$}: OperationsArgs): Generator<*, void, *> {
  let done = false

  while (!done) {
    const effect = yield race({
      submit: take(operator.actionTypes.submitOperations()),
      cancel: take(operator.actionTypes.cancelOperations()),
    })

    if (effect.submit) {
      done = yield call(handleSubmitOperations, {operations$})
    } else {
      done = true // Cancelled
    }
  }
}

export function *handleSubmitOperations ({operations$}: OperationsArgs): Generator<*, boolean, *> {
  const operations = yield select(operations$)

  const nextOperations: Array<CommentOperation> =
  yield _map(operations, function (operation: CommentOperation): Object {
      return call(handleSubmitOperation, operation)
    })

  return _every(nextOperations, function (operation: CommentOperation): boolean {
    return operation.success
  })
}

export function *handleSubmitOperation (operation: CommentOperation): Generator<*, CommentOperation, *> {
  const model: Comment = operation.model
  const currentUserUnits = yield select(currentUserUnits$)
  const crops = yield select(crops$)

  const model_converted = convertCommentsToBackendUnits(model, crops, currentUserUnits)

  const proc = operation.operation === 'ADD'
    ? controller.create
    : controller.delete
  const { error, response } = yield call([controller, proc], {
    model: model_converted,
  })

  if (error) {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: false,
      errors: response,
    }

    const action = operator.actionCreators.updateOperation(nextOperation)
    yield put(action)

    return nextOperation
  } else {
    const nextOperation = {
      ...operation,
      submitting: false,
      success: true,
    }

    const action = operator.actionCreators.cancelOperation(nextOperation)
    yield put(action)

    return nextOperation
  }
}
